'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import { UserRole } from '@/contexts/AuthContext';

export interface ImpersonatedEmployee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole;
  roles: UserRole[];
  profile_image: string;
  job_title?: string;
  department?: string;
}

interface ImpersonationContextType {
  impersonatedUser: ImpersonatedEmployee | null;
  isImpersonating: boolean;
  startImpersonation: (employee: ImpersonatedEmployee) => void;
  stopImpersonation: () => void;
  getEffectiveRoles: (actualRoles: UserRole[]) => UserRole[];
  getEffectiveIsSuperAdmin: (actualIsSuperAdmin: boolean) => boolean;
  /** Returns the effective user_profiles.id for ownership filtering.
   *  null when impersonating an unprovisioned employee (id starts with 'emp-'). */
  getEffectiveUserId: (actualUserId: string | null) => string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  impersonatedUser: null,
  isImpersonating: false,
  startImpersonation: () => {},
  stopImpersonation: () => {},
  getEffectiveRoles: (r) => r,
  getEffectiveIsSuperAdmin: (v) => v,
  getEffectiveUserId: (v) => v,
});

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedEmployee | null>(null);

  const startImpersonation = (employee: ImpersonatedEmployee) => {
    setImpersonatedUser(employee);
  };

  const stopImpersonation = () => {
    setImpersonatedUser(null);
  };

  const isImpersonating = impersonatedUser !== null;

  // When impersonating, return the impersonated user's roles instead of actual
  const getEffectiveRoles = (actualRoles: UserRole[]): UserRole[] => {
    if (isImpersonating && impersonatedUser) {
      return impersonatedUser.roles;
    }
    return actualRoles;
  };

  // SuperAdmin loses SA privileges when impersonating a non-SA user
  const getEffectiveIsSuperAdmin = (actualIsSuperAdmin: boolean): boolean => {
    if (isImpersonating && impersonatedUser) {
      return impersonatedUser.roles.includes('Admin');
    }
    return actualIsSuperAdmin;
  };

  // Returns the effective user_profiles.id for ownership filtering.
  // Unprovisioned employees have ids prefixed with 'emp-' — treat as null (no ownership).
  const getEffectiveUserId = (actualUserId: string | null): string | null => {
    if (isImpersonating && impersonatedUser) {
      const impId = impersonatedUser.id;
      // Unprovisioned employee — no user_profiles row, so no ownership
      if (!impId || impId.startsWith('emp-')) return null;
      return impId;
    }
    return actualUserId;
  };

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedUser,
        isImpersonating,
        startImpersonation,
        stopImpersonation,
        getEffectiveRoles,
        getEffectiveIsSuperAdmin,
        getEffectiveUserId,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
